import React, { memo, useState, useEffect, useMemo } from 'react'

import { useSelector } from 'react-redux'

import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
  Tooltip,
  Title,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { useToasts } from 'react-toast-notifications'
import { chartThreeMonthColors } from '../../../../constant/constant'

import Loader from '../../../../components/Loader'
import NoData from '../components/NoData'
import DashboardItemTitle from '../components/DashboardItemTitle'

import axiosInstance from '../../../../services/config/AxiosIntance'

ChartJS.register(CategoryScale, LinearScale, BarElement, Legend, Tooltip, Title)

const UsageReportRecruiter = () => {
  const { addToast } = useToasts()
  const filters = useSelector((state) => state.dashboard.filters)
  const [loading, setLoading] = useState(false)
  const [chartData, setChartData] = useState({
    labels: [''],
    datasets: [],
  })

  const getMonthRange = (startDate, endDate) => {
    const months = []
    let current = new Date(startDate)
    const end = new Date(endDate)

    while (current <= end) {
      months.push(
        current.toLocaleString('default', { month: 'long', year: 'numeric' })
      )
      const tempDate = new Date(current.setMonth(current.getMonth() + 1))
      current = tempDate
    }

    const formattedCurrent = formatDate(current)
    const formattedEnd = formatDate(end)

    if (formattedCurrent === formattedEnd) {
      months.push(
        current.toLocaleString('default', { month: 'long', year: 'numeric' })
      )
    }
    return months
  }

  const formatDate = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    return `${year}-${month}`
  }

  const fillMissingMonths = (data, selectedStartDate, selectedEndDate) => {
    const allMonths = getMonthRange(selectedStartDate, selectedEndDate)

    const resultMap = data.reduce((map, item) => {
      map[item.month] = item.activity_count
      return map
    }, {})

    const newResult = allMonths.map((month) => ({
      month,
      activity_count: resultMap[month] || 0,
    }))

    return newResult
  }

  useEffect(() => {
    setLoading(true)
    const queryParams = new URLSearchParams(filters).toString()
    axiosInstance
      .get(`/success-rate/recruiter-login-usage?${queryParams}`)
      .then((res) => {
        const newResult = fillMissingMonths(
          res.data.result,
          filters.dates[0],
          filters.dates[1]
        )
        const labelChart = [
          ...new Set(
            newResult.map((item) => item.month).filter((type) => type !== null)
          ),
        ]

        const successDataLogin = []
        const failedDataLogin = []
        const successDataSignup = []
        const failedDataSignup = []

        labelChart.forEach((month) => {
          const monthData = res.data.result.filter(
            (item) => item.month === month
          )

          let successLoginCount = 0
          let failedLoginCount = 0
          let successSignupCount = 0
          let failedSignupCount = 0

          monthData.forEach((item) => {
            if (
              item.activity_type === 'recruiter login' &&
              item.status === 'success'
            ) {
              successLoginCount += item.activity_count
            } else if (
              item.activity_type === 'recruiter login' &&
              item.status === 'failed'
            ) {
              failedLoginCount += item.activity_count
            } else if (
              item.activity_type === 'recruiter signup' &&
              item.status === 'success'
            ) {
              successSignupCount += item.activity_count
            } else if (
              item.activity_type === 'recruiter signup' &&
              item.status === 'failed'
            ) {
              failedSignupCount += item.activity_count
            }
          })

          successDataLogin.push(successLoginCount)
          failedDataLogin.push(failedLoginCount)
          successDataSignup.push(successSignupCount)
          failedDataSignup.push(failedSignupCount)
        })

        setChartData((prev) => ({
          ...prev,
          labels: labelChart,
          datasets: [
            {
              label: 'Recruiter - Successful Login',
              data: successDataLogin,
              backgroundColor: chartThreeMonthColors[0],
              borderColor: 'black',
              borderWidth: 1,
              barPercentage: 1,
            },
            {
              label: 'Recruiter - Failed Login',
              data: failedDataLogin,
              backgroundColor: chartThreeMonthColors[1],
              borderColor: 'black',
              borderWidth: 1,
              barPercentage: 1,
            },
            {
              label: 'Recruiter - Successful Signup',
              data: successDataSignup,
              backgroundColor: chartThreeMonthColors[2],
              borderColor: 'black',
              borderWidth: 1,
              barPercentage: 1,
            },
            {
              label: 'Recruiter - Failed Signup',
              data: failedDataSignup,
              backgroundColor: chartThreeMonthColors[3],
              borderColor: 'black',
              borderWidth: 1,
              barPercentage: 1,
            },
          ],
        }))
      })
      .catch((err) => {
        addToast(
          err?.response?.message ||
          err?.response?.data?.message ||
          'Internal Server Error',
          {
            appearance: 'error',
            autoDismiss: true,
          }
        )
      })
      .finally(() => {
        setLoading(false)
      })
  }, [filters])

  const options = useMemo(
    () => ({
      type: 'bar',
      data: chartData,
      maintainAspectRatio: false,
      plugins: {
        title: {
          color: 'black',
          display: true,
          align: 'start',
          position: 'top',
          text: 'Recruiter Usage Report',
          font: {
            size: 24,
            family: "'Lato', 'sans-serif'",
            weight: 300,
          },
        },
        datalabels: {
          display: true,
          color: (ctx) => ctx.dataset.backgroundColor,
          anchor: 'end',
          align: 'end',
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
        },
        y: {
          grid: {
            display: false,
          },
          ticks: {
            padding: 0.5,
          },
        },
      },
      responsive: true,
    }),
    []
  )

  const component = loading ? (
    <Loader />
  ) : chartData.labels.length ? (
    <Bar
      data={chartData}
      options={options}
      plugins={[ChartDataLabels]}
      width={50}
      height={100}
    />
  ) : (
    <>
      <DashboardItemTitle title="Recruiter Usage Report" />
      <NoData />
    </>
  )

  return component
}

export default memo(UsageReportRecruiter)
